'use client';

import { useRouter } from 'next/navigation';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { LOCAL_STORAGE_ACCESS_TOKEN_KEY, LOCAL_STORAGE_REFRESH_TOKEN_KEY } from '../constants/api';
import { apiGet, apiPost } from '../utils/api.utils';
import { useGlobal } from './GlobalProvider';

interface User {
  administratorId: string;
  username: string;
  // Ajoutez d'autres propriétés selon votre modèle utilisateur
}

interface AuthContextType {
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  user: User | null;
  isLoadingAuth: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const { showSnackbar } = useGlobal();

  const [user, setUser] = useState<User | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  const logout = () => {
    setUser(null);
    localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
    localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
    router.push('/login');
  };

  const whoami = async () => {
    const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
    if (!accessToken) {
      setIsLoadingAuth(false);
      setUser(null);
      return;
    }

    try {
      await apiGet(`auth/me`)
        .then(({ data: userData }) => {
          setUser({
            administratorId: userData.id,
            username: userData.email,
          });
        })
        .catch(() => {
          logout();
        })
        .finally(() => setIsLoadingAuth(false));
    } catch (error) {
      showSnackbar({
        severity: 'error',
        message:
          error instanceof Error
            ? error.message
            : 'Impossible de récupérer les informations utilisateur',
      });
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      return await apiPost(`auth/admin/signin`, { email, password })
        .then(async (tokens) => {
          localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, tokens.access_token);
          localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY, tokens.refresh_token);
          await whoami();
          return true;
        })
        .catch(() => false);
    } catch (error) {
      showSnackbar({
        severity: 'error',
        message: error instanceof Error ? error.message : 'Erreur de connexion',
      });
      return false;
    }
  };

  useEffect(() => {
    whoami();
  }, []);

  useEffect(() => {
    if (!user && !isLoadingAuth) {
      router.push('/login');
    }
  }, [user, isLoadingAuth]);

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoadingAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
